/**
 * Sign-up page component for user registration.
 */
import React, { useState, FormEvent } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { signup } from '../api/users';
import { useSettings } from '../contexts/SettingsContext';
import { PasswordStrengthIndicator } from '../components/PasswordStrengthIndicator';
import './SignUpPage.css';

export const SignUpPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const { settings } = useSettings();
  const navigate = useNavigate();

  // Check if sign-up is enabled
  if (!settings?.signup_enabled) {
    return (
      <div className="signup-page">
        <div className="signup-container">
          <div className="signup-header">
            <h1>Sign Up</h1>
            <p>User registration is currently disabled.</p>
            <Link to="/login" className="back-to-login">
              Back to Login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    // Validate passwords match
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      setIsLoading(false);
      return;
    }

    try {
      await signup({ email, name, password });
      setSuccess(true);
      
      // Show success message
      if (settings.signup_requires_approval) {
        // User needs approval
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } else {
        // User is active, redirect to login
        setTimeout(() => {
          navigate('/login');
        }, 2000);
      }
    } catch (err: any) {
      let errorMessage = 'Sign-up failed. Please try again.';
      
      if (err?.response?.data) {
        const errorData = err.response.data;
        
        if (Array.isArray(errorData.detail)) {
          errorMessage = errorData.detail.map((e: any) => e.msg || e.message || JSON.stringify(e)).join(', ');
        } else if (errorData.detail) {
          errorMessage = errorData.detail;
        } else if (errorData.message) {
          errorMessage = errorData.message;
        }
      } else if (err?.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
      setIsLoading(false);
    }
  };

  if (success) {
    return (
      <div className="signup-page">
        <div className="signup-container">
          <div className="signup-success">
            <h1>Account Created!</h1>
            {settings.signup_requires_approval ? (
              <>
                <p>Your account has been created and is pending admin approval.</p>
                <p>You will be able to log in once an administrator approves your account.</p>
              </>
            ) : (
              <>
                <p>Your account has been created successfully!</p>
                <p>You can now log in with your credentials.</p>
              </>
            )}
            <Link to="/login" className="back-to-login">
              Go to Login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="signup-page">
      <div className="signup-container">
        <div className="signup-header">
          <h1>Create Account</h1>
          <p>Sign up for a new account</p>
        </div>

        <form onSubmit={handleSubmit} className="signup-form">
          {error && (
            <div className="error-message" role="alert">
              <strong>Error:</strong> {error}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (error) setError('');
              }}
              required
              disabled={isLoading}
              placeholder="Enter your email"
            />
          </div>

          <div className="form-group">
            <label htmlFor="name">Full Name</label>
            <input
              id="name"
              type="text"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (error) setError('');
              }}
              required
              disabled={isLoading}
              placeholder="Enter your full name"
              minLength={1}
              maxLength={200}
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                if (error) setError('');
              }}
              required
              disabled={isLoading}
              placeholder="Enter your password"
              minLength={8}
            />
            <PasswordStrengthIndicator password={password} />
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm Password</label>
            <input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => {
                setConfirmPassword(e.target.value);
                if (error) setError('');
              }}
              required
              disabled={isLoading}
              placeholder="Confirm your password"
              minLength={8}
            />
          </div>

          <button type="submit" disabled={isLoading} className="signup-button">
            {isLoading ? 'Creating Account...' : 'Create Account'}
          </button>
        </form>

        <div className="signup-footer">
          <p>
            Already have an account? <Link to="/login">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

